import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { isSessionReady } from '~/lib/session-gate';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MissionControlSidebar } from '~/components/mission-control-sidebar';
import { AppHeader } from '~/components/app-header';
import { InspectorPanel } from '~/components/inspector-panel';
import { DevConsole } from '~/components/dev-console';
import { CommandPalette } from '~/components/command-palette';
import { ProBuilderWindowHost } from '~/features/pro-builder/pro-builder-window-host';
import { WindowSystemContext } from '~/features/windows/window-system';
import { WindowHost } from '~/features/windows/window-host';
import { WindowLayer } from '~/features/windows/window-layer';
import { WindowChooser } from '~/features/windows/window-chooser';
import { LegacyWindowHost } from '~/features/windows/legacy-window-host';
import { WhatsNewBanner } from '~/features/whats-new/whats-new-banner';
import { useUiRefactoring } from '~/lib/ui-refactoring';
import { useSeedUiRefactoring } from '~/lib/use-seed-ui-refactoring';
import { OperatorPanelHost } from '~/features/operator/operator-panel-host';
import { PageLoader } from '~/components/ui/loader';
import { useSSE } from '~/hooks/use-sse';
import { ChatProvider } from '~/features/chat/chat-context';
import { api } from '~/lib/api-client';
import { useAuthGate, type AuthMeResponse } from '~/lib/use-auth-gate';
import { NoEddyAccess } from '~/components/no-eddy-access';
import { useIsOnboarded } from '~/hooks/queries/use-tenant';
import { useDayOneState } from '~/hooks/queries/use-day-one';
import { shouldResumeDayOneChat } from '~/lib/onboarding-gate';
import { useDevSkipOnboarding, useOnboardedGate } from '~/features/onboard/use-onboarded-gate';
import { setSessionId, useSession } from '~/lib/stores/session-store';
import { pageTitle } from '~/lib/page-title';
import { useAttentionFavicon, withAttentionPrefix } from '~/features/chat/tab-attention';
import { useAttentionStore } from '~/lib/stores/attention-store';
import { reconcileAccountScope } from '~/lib/account-scope';
import { useFeatureFlag, SHOW_INSPECTOR_FLAG } from '~/hooks/queries/use-feature-flags';
import { HandsFreeCallProvider } from '~/features/voice/hands-free/hands-free-call';
import { HandsFreeCallStrip } from '~/features/voice/hands-free/hands-free-call-strip';

/**
 * FRONT-907: an onboarded user whose day-one chat is still pending (reload
 * mid-conversation, tab close) re-enters the onboarding shell — the day-one
 * chat lives there, not in the app. Explicit exits (completed/abandoned)
 * clear the pending state, so this never fires for anyone else; users from
 * before the feature have no day-one session at all.
 *
 * FRONT-1037: the rule itself lives in `~/lib/onboarding-gate`, beside the
 * dev-only `?skip-onboarding=1` switch that turns it off on a local stack.
 *
 * Pulled out of AppLayout (Aikido: single responsibility — this is
 * onboarding-specific redirect logic, not auth/chrome) into its own hook,
 * the same pattern as the shared auth gate in `~/lib/use-auth-gate`.
 */
function useDayOneRedirect(
  authed: boolean,
  onboarded: boolean | undefined,
  devSkip: boolean,
): void {
  const { data: dayOne } = useDayOneState(authed && onboarded === true);
  useEffect(() => {
    if (
      shouldResumeDayOneChat({
        authed,
        onboarded,
        sessionId: dayOne?.sessionId,
        chatStatus: dayOne?.chatStatus,
        pathname: window.location.pathname,
        devSkip,
      })
    ) {
      window.location.replace('/onboard?phase=chat');
    }
  }, [authed, onboarded, dayOne?.sessionId, dayOne?.chatStatus, devSkip]);
}


// Moved to `~/lib/session-gate` so the phone surface can share it without
// importing this shell. Re-exported here: this is where it was born, and its
// own tests import it from here.
export { isSessionReady };

export function AppLayout() {
  const auth = useAuthGate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // FRONT-732: once we know who's signed in, purge per-account client state
  // (Inspector cards persisted in localStorage) if the user changed since last
  // load — so a shared browser can't show account A's cards to account B. Keyed
  // on the authenticated user id, so it also catches a silent session-expiry →
  // different login (where the logout handler never runs).
  useEffect(() => {
    if (!auth.authed) return;
    const me = queryClient.getQueryData<AuthMeResponse>(['auth', 'me']);
    reconcileAccountScope(me?.userId);
  }, [auth.authed, queryClient]);
  // Onboarded gate: skip the query until auth resolves to avoid a 401 round-trip.
  const onboardedQuery = useIsOnboarded();
  const onboarded = auth.authed ? onboardedQuery.data?.onboarded : undefined;
  const onboardedReady = !auth.authed || onboardedQuery.isSuccess || onboardedQuery.isError;

  // Resolve the user's canonical conversation once auth + onboarding settle, so
  // every browser/machine the same user logs into opens ONE thread (their most-
  // recently-active), not each browser's own random local id. Read-only on the
  // server; we apply it to the session store and gate the chat on it below so
  // the wrong/old conversation never flashes up.
  const { sessionId: currentSessionId } = useSession();
  const sessionResolve = useQuery({
    queryKey: ['chat', 'resolve-session'],
    enabled: auth.authed && onboarded === true,
    queryFn: () => api.get<{ sessionId: string | null }>('/api/v2/chat/resolve-session'),
    staleTime: Infinity,
  });
  const resolvedSessionId = sessionResolve.data?.sessionId ?? null;
  useEffect(() => {
    if (resolvedSessionId) setSessionId(resolvedSessionId);
  }, [resolvedSessionId]);
  // Ready when resolution finished AND (it fell back to the local id, or the
  // store now holds the canonical id) — so ChatProvider mounts with the right
  // id on its very first render. Also ready (FRONT-1101) when the onboarded
  // query it depends on settled into an error, since sessionResolve can then
  // never enable — see isSessionReady above.
  const sessionReady = isSessionReady({
    sessionResolveIsError: sessionResolve.isError,
    sessionResolveIsSuccess: sessionResolve.isSuccess,
    resolvedSessionId,
    currentSessionId,
    onboardedIsError: onboardedQuery.isError,
    onboardedIsFetching: onboardedQuery.isFetching,
  });

  // FRONT-1399: the Inspector is a global panel with no per-route content, so
  // it deliberately mounts on every route including /memory. Suppressing it
  // there while leaving its header toggle live was the bug that was fixed.
  // FRONT-1323: the Inspector is going out of the product for now (MP). The
  // panel and its code stay in place — this only gates whether it mounts, so it
  // can come back later, possibly re-surfaced from Settings. Fails OPEN, so
  // merging this changes nothing until the flag is switched off in Unleash.
  const inspectorEnabled = useFeatureFlag(SHOW_INSPECTOR_FLAG, true);

  // FRONT-3223: `eddy.ui-refactoring` is seeded ONCE, before the shell
  // renders, so a surface never flashes from old to new. A failed flags
  // request seeds it off: the old UI, never a greyed-out new one.
  const uiRefactoringSeeded = useSeedUiRefactoring();
  const refactored = useUiRefactoring();

  // FRONT-2307: the tab mirrors the top bar's "asking you" count — a "(N) "
  // title prefix and an amber favicon dot (`tab-attention.ts`). Written here,
  // above the loading/redirect branches, so the route title keeps tracking
  // while they show; the count is published by the top bar, which is only
  // mounted in the full shell, so it reads 0 until then.
  const asking = useAttentionStore((s) => s.asking);
  useEffect(() => {
    document.title = withAttentionPrefix(pageTitle(location.pathname), asking);
  }, [location.pathname, asking]);
  useAttentionFavicon(asking > 0);

  useSSE({ enabled: auth.eddyAccess });

  // FRONT-1037: the dev-only skip switch, shared by both onboarding gates.
  const devSkip = useDevSkipOnboarding();
  useOnboardedGate(auth.authed, onboarded, onboardedReady, devSkip);
  useDayOneRedirect(auth.authed, onboarded, devSkip);

  // While auth resolves OR we're about to redirect, render a placeholder so
  // the chrome doesn't paint with broken-state queries firing underneath.
  if (!auth.ready || !auth.authed) {
    return <PageLoader label={!auth.ready ? 'Loading…' : 'Redirecting to login…'} />;
  }
  // FRONT-2096: signed in, but no Eddy seat. The server already refuses every
  // authenticated route for this session; the shell would only render a wall
  // of failed queries. Say what they can do instead, and who to ask.
  if (!auth.eddyAccess) {
    return <NoEddyAccess />;
  }
  if (!onboardedReady || onboarded === false) {
    return <PageLoader label={!onboardedReady ? 'Loading…' : 'Setting things up…'} />;
  }
  // Hold the chat until we know which conversation is this user's current one,
  // so a stale/old thread never flashes before the canonical one loads.
  if (!sessionReady || !uiRefactoringSeeded) {
    return <PageLoader label="Loading your conversation…" rotatingWords={refactored} />;
  }

  return (
    /*
     * Framed shell (design_handoff_shell 2 §0): the canvas colour shows
     * through a uniform `--frame-gap` seam on every edge — outer border,
     * sidebar ↔ header, sidebar ↔ main, header ↔ main, main ↔ inspector —
     * and each region below carries its own hairline + `--frame-radius`.
     * Regions used to be welded together by single shared hairlines; those
     * are gone, or every seam would read as a doubled line.
     */
    // FRONT-254: the hands-free call lives at the shell, above every page,
    // so it outlives the chat window it started in. Its strip sits above the
    // framed shell (and, by z-index, above an open chat window); with the
    // flag off neither renders anything and the shell is unchanged.
    <HandsFreeCallProvider>
    {/* FRONT-3146: chat, Build and Pro Builder windows dock into one layer over the content card.
        FRONT-3223: only with `eddy.ui-refactoring` on; off, the surfaces render standalone and
        `LegacyWindowHost` shows them one at a time, as before. */}
    <WindowSystemContext.Provider value={refactored}>
    <div className="flex h-screen flex-col bg-[var(--bg-canvas)]">
    <HandsFreeCallStrip />
    <div className="flex flex-1 min-h-0 p-[var(--frame-gap)] gap-[var(--frame-gap)]">
      <MissionControlSidebar />

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden gap-[var(--frame-gap)]">
        <AppHeader />
        {/* FRONT-2512: the release note as its own framed region under the header —
            until `eddy.ui-refactoring` moves it into Today's task list (FRONT-3147). */}
        {!refactored && <WhatsNewBanner />}
        {/*
          The Outlet fills the full slot between the sidebar and the inspector
          panel. Each page applies its own `container mx-auto` (via PageShell)
          so the *content* stays in a reading column while *chrome* like the
          page header and scrollbar reach the slot edges. The flex column
          here gives child routes a bounded height for `flex-1` / `min-h-0`.
        */}
        <div className="relative flex-1 min-h-0 overflow-hidden flex rounded-[var(--frame-radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
          {/* The page column. The window layer covers it whenever a window is shown. */}
          <div data-testid="page-column" className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {/*
            Chat lives here, not inside the inbox route, so the streaming
            fetch survives navigating to siblings (Settings/Skills/…). The
            provider sits inside the auth gate (sessionId is ready) and
            only around the Outlet — sidebar/header don't re-render on
            every streamed token.
          */}
          <ChatProvider>
            <Outlet />
            {refactored ? <WindowHost /> : <LegacyWindowHost />}
          </ChatProvider>
          </div>
          {/* FRONT-3146: the layer sits over the page, inside the card, so the
              sidebar and header stay reachable while windows are open. */}
          {refactored && <WindowLayer />}
        </div>
      </div>

      {inspectorEnabled && <InspectorPanel />}
      <DevConsole />
      <CommandPalette />
      <ProBuilderWindowHost />
      <OperatorPanelHost />
      {refactored && <WindowChooser />}
    </div>
    </div>
    </WindowSystemContext.Provider>
    </HandsFreeCallProvider>
  );
}
